import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Syringe, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddVaccinationDialog from './AddVaccinationDialog';

const VaccinationsTab = () => {
  const [vaccinations, setVaccinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchVaccinations();
  }, []);

  const fetchVaccinations = async () => {
    try {
      const response = await API.get('/medical/vaccinations');
      setVaccinations(response.data);
    } catch (error) {
      console.error('Failed to fetch vaccinations:', error);
      toast.error('Failed to load vaccinations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (vaccData) => {
    try {
      await API.post('/medical/vaccinations', vaccData);
      toast.success('Vaccination added');
      fetchVaccinations();
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vaccination');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/vaccinations/${id}`);
      toast.success('Vaccination deleted');
      fetchVaccinations();
    } catch (error) {
      toast.error('Failed to delete vaccination');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  }

  if (vaccinations.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-[#B8E6D5] rounded-full flex items-center justify-center mb-4">
            <Syringe className="h-8 w-8 text-gray-700" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No vaccinations recorded</h3>
          <p className="text-gray-600 mb-4">Track immunizations and boosters</p>
          <Button onClick={() => setShowAdd(true)} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Vaccination
          </Button>
        </div>
        <AddVaccinationDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Vaccinations</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Vaccination
        </Button>
      </div>

      <div className="space-y-4">
        {vaccinations.map((vacc) => (
          <div key={vacc.id} className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">{vacc.name}</h4>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  Received: {vacc.date_received}
                </div>
              </div>
              {vacc.booster_required && (
                <Badge className="bg-blue-100 text-blue-800">Booster Required</Badge>
              )}
            </div>

            {vacc.booster_due_date && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                <p className="text-sm font-medium text-blue-900">Booster Due: {vacc.booster_due_date}</p>
              </div>
            )}

            {vacc.adverse_reactions && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Adverse Reaction</p>
                  <p className="text-sm text-red-700">{vacc.adverse_reactions}</p>
                </div>
              </div>
            )}

            {vacc.notes && (
              <div className="p-3 bg-gray-50 rounded-lg mb-3">
                <p className="text-sm text-gray-700">{vacc.notes}</p>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => handleDelete(vacc.id)} className="text-red-600 hover:text-red-700">
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddVaccinationDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
    </>
  );
};

export default VaccinationsTab;